/**
 * DOM helpers: typed element lookup and element builders. Text always goes in
 * through textContent; nothing here parses HTML.
 */

type ElementClass<T extends Element> = new () => T;

class MissingElementError extends Error {
  override name = "MissingElementError";
}

/** The first element matching `selector` if it is a `type`, else null. */
export function find<T extends Element>(
  root: ParentNode,
  selector: string,
  type: ElementClass<T>,
): T | null {
  const element = root.querySelector(selector);
  return element instanceof type ? element : null;
}

/** Like `find`, but the element is part of the page's markup contract. */
export function get<T extends Element>(
  root: ParentNode,
  selector: string,
  type: ElementClass<T>,
): T {
  const element = find(root, selector, type);
  if (element === null) {
    throw new MissingElementError(`The page has no ${selector}`);
  }
  return element;
}

/** Every element matching `selector` that is a `type`. */
export function findAll<T extends Element>(
  root: ParentNode,
  selector: string,
  type: ElementClass<T>,
): T[] {
  return [...root.querySelectorAll(selector)].filter(
    (element): element is T => element instanceof type,
  );
}

interface ElementOptions {
  readonly className?: string;
  readonly text?: string;
  readonly attrs?: Readonly<Record<string, string>>;
}

/** Create an element with a class, text content, attributes and children. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElementOptions = {},
  ...children: readonly (Node | string)[]
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (options.className !== undefined) element.className = options.className;
  if (options.text !== undefined) element.textContent = options.text;
  for (const [name, value] of Object.entries(options.attrs ?? {})) {
    element.setAttribute(name, value);
  }
  element.append(...children);
  return element;
}

/** Show a message in an alert element (hidden when the message is empty). */
export function setMessage(element: HTMLElement, message: string): void {
  element.textContent = message;
  element.hidden = message === "";
}
